import { supabase } from '../config/supabase';
import { TABLES } from '../data/tables';
import { ShippingPolicy, MinimumOrderType } from '../data/types/domain';

export const shippingPolicyService = {
  async getShippingPolicies(): Promise<ShippingPolicy[]> {
    const { data, error } = await supabase
      .from(TABLES.SHIPPING_POLICY)
      .select('*');
    
    if (error) throw error;
    return data || [];
  },

  async getShippingPolicyById(id: number): Promise<ShippingPolicy | null> {
    const { data, error } = await supabase
      .from(TABLES.SHIPPING_POLICY)
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data || null;
  },

  async getShippingPoliciesByBusiness(businessId: number): Promise<ShippingPolicy[]> {
    const { data, error } = await supabase
      .from(TABLES.SHIPPING_POLICY)
      .select('*')
      .eq('business_id', businessId);
    
    if (error) throw error;
    return data || [];
  },

  async createShippingPolicy(policy: Omit<ShippingPolicy, 'id' | 'created_at' | 'updated_at'>): Promise<ShippingPolicy> {
    const { data, error } = await supabase
      .from(TABLES.SHIPPING_POLICY)
      .insert([{
        ...policy,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateShippingPolicy(
    id: number,
    updates: Partial<Omit<ShippingPolicy, 'id' | 'created_at' | 'updated_at'>>
  ): Promise<ShippingPolicy> {
    const { data, error } = await supabase
      .from(TABLES.SHIPPING_POLICY)
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async deleteShippingPolicy(id: number): Promise<void> {
    const { error } = await supabase
      .from(TABLES.SHIPPING_POLICY)
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  async deleteShippingPolicies(businessId: number): Promise<void> {
    const { error } = await supabase
      .from(TABLES.SHIPPING_POLICY)
      .delete()
      .eq('business_id', businessId); 
    
    if (error) throw error;
  },

  async updateMinimumOrderType(id: number, minimumOrderType: MinimumOrderType): Promise<ShippingPolicy> {
    return this.updateShippingPolicy(id, { minimum_order_type: minimumOrderType });
  },

  async updateMinimumOrderAmount(id: number, amount: number): Promise<ShippingPolicy> {
    return this.updateShippingPolicy(id, { minimum_order_amount: amount });
  },

  async updateMinimumPerKgPrice(id: number, price: number): Promise<ShippingPolicy> {
    return this.updateShippingPolicy(id, { minimum_per_kg_price: price });
  },

  async updateCurrency(id: number, currency: string): Promise<ShippingPolicy> {
    return this.updateShippingPolicy(id, { currency });
  },

  async updateFromCountry(id: number, fromCountry: number): Promise<ShippingPolicy> {
    return this.updateShippingPolicy(id, { from_country: fromCountry });
  },

  async updateToCountry(id: number, toCountry: number): Promise<ShippingPolicy> {
    return this.updateShippingPolicy(id, { to_country: toCountry });
  },
}; 